import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";

export interface TechnicianOption {
  id: string;
  name: string;
}

export interface TechnicianChoiceDialogSubtitle {
  roNumber?: string;
  vehicleDescription?: string;
}

/**
 * A hook providing an imperative technician-choice picker for the move-time
 * technician write-back popup — sibling to `useLabelChoiceDialog`
 * (`src/components/Common/LabelChoiceDialog.tsx`), not a shared component
 * with it: label options are self-describing strings, technician options
 * need an id/name pair (display "John Smith", resolve to "12345"), so the
 * prop shapes genuinely differ. Copies the same hook-stability pattern that
 * hook's own doc comment explains (see the WEBV3-237 design spec's risk #3)
 * for the same reason: this hook is used from `Dashboard.tsx`, which
 * re-renders on every live board update, and an unstable returned `Dialog`
 * reference would remount the picker mid-choice.
 *
 * `chooseTechnician`'s promise resolves with the chosen technician id
 * ("Sync and move"), resolves with `null` ("Don't Assign, just move" —
 * WEBV3-292: complete the move without writing anything to Tekmetric), or
 * rejects with no value (Cancel/Esc/click-outside — aborts the whole move).
 * @returns {object} `chooseTechnician` and the `Dialog` component to render.
 */
export function useTechnicianChoiceDialog() {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [options, setOptions] = useState<TechnicianOption[]>([]);
  const [subtitle, setSubtitle] = useState<TechnicianChoiceDialogSubtitle>({});
  const [handlers, setHandlers] = useState<{
    onChoose: (id: string) => void;
    onDecline: () => void;
    onCancel: () => void;
  }>({
    onChoose: () => {},
    onDecline: () => {},
    onCancel: () => {},
  });

  const chooseTechnician = useCallback(
    (
      candidateOptions: TechnicianOption[],
      nextSubtitle: TechnicianChoiceDialogSubtitle = {}
    ) => {
      return new Promise<string | null>((resolve, reject) => {
        setOptions(candidateOptions);
        setSubtitle(nextSubtitle);
        setHandlers({
          onChoose: (id) => {
            resolve(id);
            setDialogOpen(false);
          },
          onDecline: () => {
            resolve(null);
            setDialogOpen(false);
          },
          onCancel: () => {
            reject();
            setDialogOpen(false);
          },
        });
        setDialogOpen(true);
      });
    },
    []
  );

  const PickerDialog = useMemo(
    () => () => (
      <TechnicianChoiceDialog
        open={isDialogOpen}
        setOpen={setDialogOpen}
        options={options}
        subtitle={subtitle}
        onChoose={handlers.onChoose}
        onDecline={handlers.onDecline}
        onCancel={handlers.onCancel}
      />
    ),
    [isDialogOpen, options, subtitle, handlers]
  );

  return { chooseTechnician, Dialog: PickerDialog };
}

export interface TechnicianChoiceDialogProps {
  options: TechnicianOption[];
  subtitle?: TechnicianChoiceDialogSubtitle;
  onChoose: (id: string) => void;
  onDecline: () => void;
  onCancel: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const TechnicianChoiceDialog = ({
  options,
  subtitle = {},
  onChoose,
  onDecline,
  onCancel,
  open = false,
  setOpen,
}: TechnicianChoiceDialogProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelectedId(options.length === 1 ? options[0].id : null);
  }, [open, options]);

  const subtitleText = [
    subtitle.roNumber ? `RO ${subtitle.roNumber}` : null,
    subtitle.vehicleDescription || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
        setOpen(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Which technician should we sync back to Tekmetric?
          </DialogTitle>
          {subtitleText && (
            <DialogDescription>{subtitleText}</DialogDescription>
          )}
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {options.map((option) => (
            <label key={option.id} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="technician-choice"
                checked={selectedId === option.id}
                onChange={() => setSelectedId(option.id)}
              />
              {option.name}
            </label>
          ))}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch sm:space-x-0">
          <Button
            type={"button"}
            variant={"secondary"}
            className="w-full"
            onClick={onDecline}
          >
            Don&apos;t Assign, just move
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type={"button"}
              variant={"secondary"}
              className="w-full"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type={"button"}
              className="w-full"
              disabled={!selectedId}
              onClick={() => selectedId && onChoose(selectedId)}
            >
              Sync and move
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
